import { useState, useEffect } from "react";
import { useAuth } from "@context/AuthContext";
import { Modal, ModalFooter } from "@common/Modal";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";

const post = (ep, fields) => { const fd=new FormData(); Object.entries(fields).forEach(([k,v])=>fd.append(k,v)); return axios.post(`${API_BASE}${ep}`,fd).then(r=>r.data).catch(()=>null); };

function InputField({ label, value, onChange, onBlur, error, type="text", textarea, placeholder }) {
  const borderColor = error ? "#DC2626" : "var(--fc-border)";
  const boxShadow = error ? "0 0 0 3px rgba(220,38,38,0.08)" : "none";
  const commonStyle = { width:"100%", boxSizing:"border-box", padding:"10px 12px", border:`1.5px solid ${borderColor}`, borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", color:"var(--fc-text-primary)", background:"var(--fc-card-bg)", boxShadow, transition:"all 0.18s" };
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</label>
      {textarea ? (
        <textarea value={value} onChange={e=>onChange(e.target.value)} onBlur={onBlur} placeholder={placeholder||label} rows={4}
          style={{ ...commonStyle, resize:"vertical" }}
          onFocus={e=>{ if(!error){ e.target.style.borderColor="#2563EB"; } }} />
      ) : (
        <input type={type} value={value} onChange={e=>onChange(e.target.value)} onBlur={onBlur} placeholder={placeholder||label}
          style={commonStyle}
          onFocus={e=>{ if(!error){ e.target.style.borderColor="#2563EB"; e.target.style.boxShadow="0 0 0 3px rgba(79,70,229,0.1)"; } }} />
      )}
      {error && <div style={{ fontSize:11.5, color:"#DC2626", marginTop:5, display:"flex", alignItems:"center", gap:4 }}><i className="fa-solid fa-circle-exclamation" style={{fontSize:10}} />{error}</div>}
    </div>
  );
}

function PasswordSection({ userSlug }) {
  const [form, setForm]     = useState({ oldPassword:"", newPassword:"", confirmPassword:"" });
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [success, setSuccess] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const validateField = (key, values = form) => {
    if (key === "oldPassword") {
      if (!values.oldPassword.trim()) return "Enter your current password";
    }
    if (key === "newPassword") {
      if (!values.newPassword.trim()) return "Enter a new password";
      if (values.newPassword.trim().length < 6) return "Must be at least 6 characters";
    }
    if (key === "confirmPassword") {
      if (!values.confirmPassword.trim()) return "Confirm your new password";
      if (values.confirmPassword.trim() !== values.newPassword.trim()) return "Doesn't match new password";
    }
    return "";
  };

  const set = (k, v) => {
    const next = { ...form, [k]: v };
    setForm(next);
    // live-clear/update errors for fields already touched, so fixing a typo
    // clears the message immediately instead of waiting for the next blur
    if (touched[k]) setFieldErrors(fe => ({ ...fe, [k]: validateField(k, next) }));
    if (k === "newPassword" && touched.confirmPassword) {
      setFieldErrors(fe => ({ ...fe, confirmPassword: validateField("confirmPassword", next) }));
    }
  };

  const handleBlur = (k) => {
    setTouched(t => ({ ...t, [k]: true }));
    setFieldErrors(fe => ({ ...fe, [k]: validateField(k) }));
  };

  const validateAll = () => {
    const errs = {
      oldPassword: validateField("oldPassword"),
      newPassword: validateField("newPassword"),
      confirmPassword: validateField("confirmPassword"),
    };
    setFieldErrors(errs);
    setTouched({ oldPassword:true, newPassword:true, confirmPassword:true });
    return Object.values(errs).every(e => !e);
  };

  const handleSubmit = () => {
    setError(""); setSuccess("");
    if (!validateAll()) return;
    setConfirmOpen(true);
  };

  const doUpdate = async () => {
    setConfirmOpen(false);
    setBusy(true); setError(""); setSuccess("");
    const res = await post("update-profile-password", {
      user_slug:    userSlug,
      old_password: form.oldPassword,
      new_password: form.newPassword,
    });
    setBusy(false);
    if (res?.status === 1) {
      setSuccess(res.message || "Password updated!");
      setForm({ oldPassword:"", newPassword:"", confirmPassword:"" });
      setTouched({}); setFieldErrors({});
    } else {
      setError(res?.message || "Unable to update password.");
    }
    setTimeout(() => { setSuccess(""); setError(""); }, 4000);
  };

  return (
    <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
      <h2 style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:14, paddingBottom:10, borderBottom:"1px solid var(--fc-border-light)" }}>Password</h2>

      {success && <div style={{ padding:"10px 12px", background:"var(--fc-green-light)", border:"1px solid var(--fc-green-border)", borderRadius:9, marginBottom:14, fontSize:13, color:"var(--fc-green-text)" }}><i className="fa-solid fa-circle-check" style={{marginRight:7}} />{success}</div>}
      {error   && <div style={{ padding:"10px 12px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:9, marginBottom:14, fontSize:13, color:"#DC2626" }}><i className="fa-solid fa-circle-exclamation" style={{marginRight:7}} />{error}</div>}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0 14px" }}>
        <InputField label="Current Password" type="password" value={form.oldPassword}
          onChange={v=>set("oldPassword",v)} onBlur={()=>handleBlur("oldPassword")} error={touched.oldPassword && fieldErrors.oldPassword} />
        <InputField label="New Password" type="password" value={form.newPassword}
          onChange={v=>set("newPassword",v)} onBlur={()=>handleBlur("newPassword")} error={touched.newPassword && fieldErrors.newPassword} />
        <InputField label="Confirm New Password" type="password" value={form.confirmPassword}
          onChange={v=>set("confirmPassword",v)} onBlur={()=>handleBlur("confirmPassword")} error={touched.confirmPassword && fieldErrors.confirmPassword} />
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end" }}>
        <button onClick={handleSubmit} disabled={busy} className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill" style={{ minWidth:160 }}>
          {busy ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:12}} /> Updating...</> : "Update Password"}
        </button>
      </div>

      {confirmOpen && (
        <Modal title="Change password?" onClose={()=>setConfirmOpen(false)} maxWidth={380}
          footer={<ModalFooter onCancel={()=>setConfirmOpen(false)} onConfirm={doUpdate} confirmLabel="Yes, change it" />}>
          <p style={{ fontSize:13, color:"var(--fc-text-secondary)" }}>You'll need your new password the next time you log in.</p>
        </Modal>
      )}
    </div>
  );
}

export default function AccountSettingsPage() {
  const { userSlug } = useAuth();

  const [loading,    setLoading]    = useState(true);
  const [form,        setForm]      = useState({ firstName:"", lastName:"", jobRole:"", bio:"" });
  const [touched,     setTouched]   = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [loginEmail,  setLoginEmail]= useState("");
  const [avatar,      setAvatar]    = useState(null);
  const [busy,        setBusy]      = useState(false);
  const [avatarBusy,  setAvatarBusy]= useState(false);
  const [success,     setSuccess]   = useState("");
  const [error,       setError]     = useState("");

  const validateField = (key, values = form) => {
    if (key === "firstName" && !values.firstName.trim()) return "First name is required";
    if (key === "lastName"  && !values.lastName.trim())  return "Last name is required";
    return "";
  };

  const set = (k, v) => {
    const next = { ...form, [k]: v };
    setForm(next);
    if (touched[k]) setFieldErrors(fe => ({ ...fe, [k]: validateField(k, next) }));
  };

  const handleBlur = (k) => {
    setTouched(t => ({ ...t, [k]: true }));
    setFieldErrors(fe => ({ ...fe, [k]: validateField(k) }));
  };

  const validateAll = () => {
    const keys = ["firstName", "lastName"];
    const errs = {};
    keys.forEach(k => { errs[k] = validateField(k); });
    setFieldErrors(errs);
    setTouched(t => ({ ...t, firstName:true, lastName:true }));
    return keys.every(k => !errs[k]);
  };

  useEffect(() => {
    if (!userSlug) { setLoading(false); return; }
    post("get-user-details", { user_slug: userSlug }).then(data => {
      const d = data?.user_details;
      if (d) {
        setLoginEmail(d.login_details?.user_email || "");
        setForm({
          firstName:   d.profile_details?.first_name  || "",
          lastName:    d.profile_details?.last_name   || "",
          jobRole:     d.profile_details?.user_profile_role || "",
          bio:         d.profile_details?.profile_bio  || "",
        });
        const img = d.profile_details?.profile_image_path;
        if (img) setAvatar(PROFILE_IMG_BASE + img);
      }
      setLoading(false);
    });
  }, [userSlug]);

  const handleSaveProfile = async () => {
    setError(""); setSuccess("");
    if (!validateAll()) return;
    setBusy(true);
    const basicRes = await post("update-profile-basic-info", {
      current_user_slug: userSlug,
      user_slug:         userSlug,
      first_name:        form.firstName,
      last_name:         form.lastName,
      user_profile_role: form.jobRole,
      profile_bio:       form.bio,
      report_to:         "",
    });
    setBusy(false);
    if (basicRes?.status === 1) {
      setSuccess("Profile updated!");
    } else {
      setError(basicRes?.message || "Update failed. Please try again.");
    }
    setTimeout(() => { setSuccess(""); setError(""); }, 4000);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatar(URL.createObjectURL(file));
    setAvatarBusy(true);
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("file", file);
    const res = await axios.post(`${API_BASE}user-avatar-upload`, fd).then(r=>r.data).catch(()=>null);
    setAvatarBusy(false);
    if (res?.status === 1) setSuccess("Avatar updated!");
    else setError("Avatar upload failed.");
    setTimeout(() => { setSuccess(""); setError(""); }, 4000);
  };

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:800, color:"var(--fc-text-primary)", margin:0 }}>Profile Settings</h1>
        <p style={{ fontSize:13, color:"var(--fc-text-muted)", margin:"3px 0 0" }}>Update your personal, contact, and account information</p>
      </div>

      {loading ? (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:28 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {[1,2,3,4].map(i => <div key={i} style={{ height:44, background:"var(--fc-border-light)", borderRadius:9 }} />)}
          </div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:16, maxWidth:640 }}>

          {/* Login info (read-only) */}
          <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
            <h2 style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:14, paddingBottom:10, borderBottom:"1px solid var(--fc-border-light)" }}>Login Information</h2>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}>Login Email</label>
              <input value={loginEmail} disabled
                style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:"1.5px solid var(--fc-border)", borderRadius:9, fontSize:13, color:"var(--fc-text-subtle)", background:"var(--fc-bg-light)", fontFamily:"inherit" }} />
              <div style={{ fontSize:11, color:"var(--fc-text-subtle)", marginTop:4 }}>Login email cannot be changed.</div>
            </div>
          </div>

          {/* Profile + contact details */}
          <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:24, boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
            <h2 style={{ fontSize:14, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:14, paddingBottom:10, borderBottom:"1px solid var(--fc-border-light)" }}>Profile Information</h2>

            <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:20 }}>
              <div style={{ width:64, height:64, borderRadius:14, background:"var(--fc-border-light)", border:"2px solid var(--fc-border)", overflow:"hidden", flexShrink:0 }}>
                {avatar ? <img src={avatar} alt="Avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}><i className="fa-solid fa-user" style={{ fontSize:24, color:"var(--fc-text-subtle)" }} /></div>}
              </div>
              <div>
                <label htmlFor="avatar-upload">
                  <div className="fc-btn fc-btn-sm fc-btn-secondary fc-btn-pill" style={{ display:"inline-flex", alignItems:"center", gap:6, cursor:"pointer" }}>
                    {avatarBusy ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:10}} /> Uploading...</> : <><i className="fa-solid fa-upload" style={{fontSize:10}} /> Upload Photo</>}
                  </div>
                </label>
                <input id="avatar-upload" type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display:"none" }} />
              </div>
            </div>

            {success && <div style={{ padding:"10px 12px", background:"var(--fc-green-light)", border:"1px solid var(--fc-green-border)", borderRadius:9, marginBottom:14, fontSize:13, color:"var(--fc-green-text)" }}><i className="fa-solid fa-circle-check" style={{marginRight:7}} />{success}</div>}
            {error   && <div style={{ padding:"10px 12px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:9, marginBottom:14, fontSize:13, color:"#DC2626" }}><i className="fa-solid fa-circle-exclamation" style={{marginRight:7}} />{error}</div>}

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
              <InputField label="First Name" value={form.firstName} onChange={v=>set("firstName",v)} onBlur={()=>handleBlur("firstName")} error={touched.firstName && fieldErrors.firstName} />
              <InputField label="Last Name"  value={form.lastName}  onChange={v=>set("lastName",v)}  onBlur={()=>handleBlur("lastName")}  error={touched.lastName && fieldErrors.lastName} />
            </div>
            <InputField label="Job Role / Title" value={form.jobRole} onChange={v=>set("jobRole",v)} onBlur={()=>handleBlur("jobRole")} error={touched.jobRole && fieldErrors.jobRole} />

            <div style={{ display:"flex", justifyContent:"flex-end" }}>
              <button onClick={handleSaveProfile} disabled={busy} className="fc-btn fc-btn-md fc-btn-primary fc-btn-pill" style={{ minWidth:160 }}>
                {busy ? <><i className="fa-solid fa-spinner fa-spin" style={{fontSize:12}} /> Saving...</> : "Update Profile"}
              </button>
            </div>
          </div>

          <PasswordSection userSlug={userSlug} />
        </div>
      )}
    </div>
  );
}
